/**
 * KeyboardLayoutPicker Component
 * Modern visual keyboard layout for selecting shortcut keys
 * Realistic 3D keycap styling with proper spacing and grouping
 */

import React, { useState } from 'react';
import { KEYBOARD_LAYOUT, MODIFIER_HIGHLIGHT_KEYS, RESERVED_KEYS, keyToDisplay } from '../../config/shortcutConfig';

const KeyboardLayoutPicker = ({
  mode = 'key',
  selectedKey = null,
  disabledKeys = [],
  onKeySelect,
  title = 'Select a Key'
}) => {
  const isModifierMode = mode === 'modifier';
  const [hoveredKey, setHoveredKey] = useState(null);

  const getKeyClasses = (keyObj) => {
    const isSelected = selectedKey === keyObj.key;
    const isDisabled = disabledKeys.includes(keyObj.key) || RESERVED_KEYS.includes(keyObj.key);
    const isModifier = MODIFIER_HIGHLIGHT_KEYS.includes(keyObj.key);
    const isHovered = hoveredKey === keyObj.key;
    const isWide = keyObj.width > 1.2;

    // Base key style - realistic keycap
    let base = 'relative rounded-md border font-medium transition-all duration-100 select-none ';

    // In modifier mode, dim non-modifier keys
    if (isModifierMode && !isModifier) {
      return base + 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed opacity-40';
    }

    if (isSelected) {
      return base + 'bg-gradient-to-b from-indigo-500 to-indigo-700 text-white border-indigo-800 shadow-[0_2px_0_0_rgba(49,46,129,0.8),0_4px_8px_rgba(99,102,241,0.4)] scale-[0.97] cursor-pointer ring-2 ring-indigo-300 ring-offset-1';
    }

    if (isDisabled) {
      return base + 'bg-gradient-to-b from-red-50 to-red-100 text-red-300 border-red-200 cursor-not-allowed shadow-[0_2px_0_0_rgba(252,165,165,0.5)]';
    }

    if (isModifierMode && isModifier) {
      return base + (isHovered
        ? 'bg-gradient-to-b from-purple-100 to-purple-200 text-purple-800 border-purple-400 shadow-[0_2px_0_0_rgba(147,51,234,0.3),0_4px_12px_rgba(147,51,234,0.15)] cursor-pointer scale-[1.03]'
        : 'bg-gradient-to-b from-purple-50 to-purple-100 text-purple-700 border-purple-300 shadow-[0_2px_0_0_rgba(168,85,247,0.3)] cursor-pointer');
    }

    // Modifier keys get a slightly different base style
    if (isModifier) {
      return base + (isHovered
        ? 'bg-gradient-to-b from-gray-50 to-gray-200 text-gray-800 border-gray-400 shadow-[0_2px_0_0_rgba(107,114,128,0.4),0_4px_12px_rgba(107,114,128,0.15)] cursor-pointer scale-[1.02]'
        : 'bg-gradient-to-b from-gray-100 to-gray-200 text-gray-600 border-gray-300 shadow-[0_2px_0_0_rgba(156,163,175,0.5)] cursor-pointer');
    }

    // Standard keys
    if (isHovered) {
      return base + 'bg-gradient-to-b from-white to-indigo-50 text-indigo-700 border-indigo-300 shadow-[0_2px_0_0_rgba(99,102,241,0.3),0_4px_12px_rgba(99,102,241,0.12)] cursor-pointer scale-[1.03]';
    }

    return base + 'bg-gradient-to-b from-white to-gray-50 text-gray-700 border-gray-300 shadow-[0_2px_0_0_rgba(209,213,219,0.8)] cursor-pointer hover:shadow-[0_2px_0_0_rgba(156,163,175,0.6)]';
  };

  const handleClick = (keyObj) => {
    if (RESERVED_KEYS.includes(keyObj.key)) return;
    if (disabledKeys.includes(keyObj.key)) return;
    if (isModifierMode && !MODIFIER_HIGHLIGHT_KEYS.includes(keyObj.key)) return;
    onKeySelect(keyObj.key);
  };

  // Separate F-key row from main keys, arrow/nav keys, and numpad
  const fKeyRow = KEYBOARD_LAYOUT[0];    // Esc + F1-F12
  const mainRows = KEYBOARD_LAYOUT.slice(1, 6); // Number row through bottom row
  const navRow = KEYBOARD_LAYOUT[6];     // Arrows + navigation
  const numpadKeys = KEYBOARD_LAYOUT[7]; // Numpad keys

  const renderKey = (keyObj) => {
    const isWide = keyObj.width > 1.2;
    const fontSize = keyObj.width >= 2 ? 'text-[10px]' : keyObj.label.length > 3 ? 'text-[9px]' : 'text-[11px]';
    const isDisabled = disabledKeys.includes(keyObj.key);

    return (
      <button
        key={keyObj.key}
        onClick={() => handleClick(keyObj)}
        onMouseEnter={() => setHoveredKey(keyObj.key)}
        onMouseLeave={() => setHoveredKey(null)}
        className={`${getKeyClasses(keyObj)} ${fontSize} flex items-center justify-center`}
        style={{
          width: `${keyObj.width * 36}px`,
          height: '32px',
          padding: '0 3px',
        }}
        title={
          isDisabled ? `"${keyObj.label}" is already assigned`
          : RESERVED_KEYS.includes(keyObj.key) ? `"${keyObj.label}" is reserved`
          : keyObj.key
        }
      >
        <span className={isDisabled ? 'line-through opacity-60' : ''}>
          {keyObj.label}
        </span>
      </button>
    );
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</span>
        {selectedKey && (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400">Selected:</span>
            <kbd className="px-2 py-0.5 bg-indigo-600 text-white rounded text-xs font-mono font-bold shadow-sm">
              {keyToDisplay(selectedKey)}
            </kbd>
          </div>
        )}
      </div>

      {/* Keyboard Container */}
      <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-xl p-3 border border-gray-300 shadow-inner">
        {/* F-Key Row */}
        <div className="flex gap-[3px] mb-2 justify-center">
          {fKeyRow.map(renderKey)}
        </div>

        {/* Separator line */}
        <div className="border-t border-gray-300/60 mb-2" />

        {/* Main Keyboard Rows */}
        <div className="space-y-[3px]">
          {mainRows.map((row, rowIdx) => (
            <div key={`main-${rowIdx}`} className="flex gap-[3px] justify-center">
              {row.map(renderKey)}
            </div>
          ))}
        </div>

        {/* Separator line */}
        <div className="border-t border-gray-300/60 my-2" />

        {/* Navigation & Arrow Keys + Numpad side by side */}
        <div className="flex gap-4 justify-center">
          {/* Arrow + Nav Keys */}
          <div className="flex gap-[3px] items-center">
            {navRow.map(renderKey)}
          </div>

          {/* Numpad Separator */}
          <div className="border-l border-gray-300/60" />

          {/* Numpad */}
          <div className="flex flex-col items-center">
            <span className="text-[9px] text-gray-400 font-semibold uppercase tracking-wider mb-1">Numpad</span>
            <div className="grid grid-cols-4 gap-[3px]">
              {/* Row 1: NumLock / * − */}
              {numpadKeys.slice(0, 4).map(renderKey)}
              {/* Row 2: 7 8 9 + */}
              {numpadKeys.slice(4, 8).map(renderKey)}
              {/* Row 3: 4 5 6 (empty for + spanning) */}
              {numpadKeys.slice(8, 11).map(renderKey)}
              <div /> {/* empty cell next to 4,5,6 */}
              {/* Row 4: 1 2 3 Enter */}
              {numpadKeys.slice(11, 15).map(renderKey)}
              {/* Row 5: 0 (wide) . */}
              {numpadKeys.slice(15).map(renderKey)}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-[10px] text-gray-500 px-1">
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-3.5 h-3.5 rounded bg-gradient-to-b from-indigo-500 to-indigo-700 border border-indigo-800 shadow-sm" />
          <span>Selected</span>
        </div>
        {disabledKeys.length > 0 && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 h-3.5 rounded bg-gradient-to-b from-red-50 to-red-100 border border-red-200 shadow-sm" />
            <span>Already assigned</span>
          </div>
        )}
        {isModifierMode && (
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 h-3.5 rounded bg-gradient-to-b from-purple-50 to-purple-100 border border-purple-300 shadow-sm" />
            <span>Modifier keys</span>
          </div>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          <span className="inline-block w-3.5 h-3.5 rounded bg-gray-100 border border-gray-200 opacity-50" />
          <span>Reserved</span>
        </div>
      </div>
    </div>
  );
};

export default KeyboardLayoutPicker;
